import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useCustomerWorkspace } from "../../../context/CustomerWorkspaceContext";
import {
  updateAgentInstructions,
} from "../../../services/agentService";
import TemplateReference from "../../TemplateReference";
import { useAgentDetails } from "./hooks/useAgentDetails";
import { useAgentKnowledgeFiles } from "./hooks/useAgentKnowledgeFiles";
import KnowledgeFilesSection from "./components/KnowledgeFilesSection";
import ToolsSelectionSection from "./components/ToolsSelectionSection";
import ConversationStartersSection from "./components/ConversationStartersSection";
import ModelSelectionSection from "./components/ModelSelectionSection";
import MemorySection from "./components/MemorySection";
import AgentProfilePictureUploader from "./components/AgentProfilePictureUploader";
import { generatePresignedUrl, uploadFileToS3 } from "../../../services/contentService";
import { CognitoAuthService } from "../../../services/cognitoService";
import { editableModelFor } from "../ChatWithAgent/config/modelOptions";
import { debug } from "../../../utils/debugLogger";
import { sanitizeFileName } from "../../../utils/fileUtils";

const getErrorMessage = (error: unknown): string =>
  error instanceof Error && error.message ? error.message : "please try again";

const EditAgentForm: React.FC = () => {
  const { idToken } = useAuth();
  const { selectedCustomerId, selectedCustomerName } = useCustomerWorkspace();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const urlAgentId = searchParams.get("agentId") ?? "";
  const urlCustomerId = searchParams.get("customerId") ?? "";
  const urlThreadId = searchParams.get("threadId") ?? "";

  // Use URL param customer if present, fall back to sidebar context
  const effectiveCustomerId = urlCustomerId || selectedCustomerId || "";

  // Minimal customer object the hooks need
  const selectedCustomer = useMemo(
    () => (effectiveCustomerId ? { customer_id: effectiveCustomerId } : null),
    [effectiveCustomerId]
  );

  const selectedAgentId = urlAgentId;

  // Form state
  const [instructions, setInstructions] = useState<string>("");
  const [agentName, setAgentName] = useState<string>("");
  const [model, setModel] = useState<string>("");
  const [conversationStarters, setConversationStarters] = useState<
    Array<{ id: string; suggestion: string }>
  >([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [pictureUrl, setPictureUrl] = useState<string>("");
  const [newPicture, setNewPicture] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [showTemplate, setShowTemplate] = useState<boolean>(false);

  const {
    loading: loadingAgentDetails,
    agentDetails,
    parsedDetails,
    error: agentDetailsError,
  } = useAgentDetails({ selectedAgentId, selectedCustomer, idToken });

  const {
    knowledgeFiles,
    newKnowledgeFiles,
    savingKnowledgeFiles,
    handleSaveKnowledgeFiles,
    removeKnowledgeFile,
    addKnowledgeFiles,
    removeNewFile,
    initializeKnowledgeFiles,
    resetKnowledgeFiles,
  } = useAgentKnowledgeFiles({
    selectedCustomer,
    selectedAgentId,
    agentDetails,
    idToken,
    onMessageChange: setMessage,
  });

  // Populate form when agent details load
  useEffect(() => {
    if (parsedDetails) {
      setInstructions(parsedDetails.instructions);
      setAgentName(parsedDetails.agentName);
      // A retired stored model opens as the recommended one, so Save migrates it (SGL-423).
      setModel(editableModelFor(parsedDetails.model, parsedDetails.agentType, agentDetails?.provider));
      setConversationStarters(parsedDetails.conversationStarters);
      setSelectedTools(parsedDetails.selectedTools);
      setPictureUrl(parsedDetails.profilePictureUrl || "");
      setNewPicture(null);
      initializeKnowledgeFiles(parsedDetails.rawDetails);
    } else if (!selectedAgentId) {
      setInstructions("");
      setAgentName("");
      setModel("");
      setConversationStarters([]);
      setSelectedTools([]);
      resetKnowledgeFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedDetails, selectedAgentId]);

  const handleToolToggle = (toolId: string) => {
    setSelectedTools((prev) =>
      prev.includes(toolId) ? prev.filter((id) => id !== toolId) : [...prev, toolId]
    );
  };

  const handleSubmit = async () => {
    const trimmedName = agentName.trim();
    if (!selectedCustomer || !selectedAgentId || !trimmedName || !instructions || !agentDetails) {
      setMessage("Please provide a name and instructions.");
      return;
    }

    const assistantId = agentDetails.openai_assistant_id || "";
    const provider = agentDetails.provider || "openai";

    setLoading(true);
    setMessage("Updating agent...");

    // A new picture is uploaded first; its CloudFront URL becomes the agent's picture
    // (avatars render profile_picture_url directly as an <img> src).
    let profilePictureUrl: string | undefined;
    if (newPicture) {
      try {
        setMessage("Uploading profile picture...");
        const presigned = await generatePresignedUrl(
          {
            customer_id: selectedCustomer.customer_id,
            user_email: CognitoAuthService.getUserEmail(idToken),
            is_image: true,
            is_training_data: false,
            training_data_content_type: "",
            // The key becomes part of the stored URL, so no spaces or odd characters.
            file_name: sanitizeFileName(newPicture.name),
            image_content_type: newPicture.type,
            video_content_type: "",
            image_type: "profile",
            video_type: "",
            logo: false,
          },
          idToken
        );
        // The presign endpoint answers 200 with { error } for a bad request.
        const upload = presigned?.image;
        if (!upload?.uploadURL || !upload?.cloudFrontURL) {
          throw new Error(presigned?.error || "no upload URL was returned");
        }
        await uploadFileToS3(upload.uploadURL, newPicture);
        profilePictureUrl = upload.cloudFrontURL;
      } catch (error) {
        debug.error("Error uploading agent profile picture:", error);
        setMessage(`Couldn't upload the picture: ${getErrorMessage(error)}`);
        setLoading(false);
        return;
      }
      setMessage("Updating agent...");
    }

    try {
      await updateAgentInstructions(
        selectedAgentId,
        selectedCustomer.customer_id,
        assistantId || undefined,
        trimmedName,
        instructions,
        provider,
        idToken,
        selectedTools.length > 0 ? selectedTools : undefined,
        profilePictureUrl,
        undefined,
        model || undefined,
        conversationStarters.length > 0 ? conversationStarters : undefined
      );
      if (profilePictureUrl) {
        setPictureUrl(profilePictureUrl);
        setNewPicture(null);
      }
      setMessage("✅ Agent updated successfully!");
    } catch (error) {
      debug.error("Error updating agent instructions:", error);
      setMessage(`Couldn't save the agent: ${getErrorMessage(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const hasKnowledgeFileChanges: boolean =
    newKnowledgeFiles.length > 0 ||
    (parsedDetails !== null &&
      parsedDetails.knowledgeFiles.some(
        (originalFile) => !knowledgeFiles.some((f) => f.key === originalFile.key)
      )) ||
    false;

  const isLoading = loading || loadingAgentDetails;
  const isDisabled = !agentDetails || isLoading || savingKnowledgeFiles;

  const handleBackToChat = () => {
    const cid = effectiveCustomerId;
    if (selectedAgentId && cid) {
      const params = new URLSearchParams({ agentId: selectedAgentId, customerId: cid });
      if (urlThreadId) params.set("threadId", urlThreadId);
      navigate(`/?${params.toString()}`);
    } else {
      navigate("/");
    }
  };

  const displayCustomerName =
    selectedCustomerName ||
    (urlCustomerId ? urlCustomerId : null);

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
      {/* Back button */}
      <div className="mb-6">
        <button
          type="button"
          onClick={handleBackToChat}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-[#2f4941] transition-colors hover:bg-zinc-100"
        >
          <span className="material-icons-outlined text-lg">arrow_back</span>
          Back to chat
        </button>
      </div>

      <h1 className="text-xl font-bold mb-6">Edit Agent Instructions</h1>

      {/* Business Profile (read-only, from sidebar) */}
      <div className="mb-6 flex items-center gap-3 rounded-lg border border-theme-border bg-theme-surface px-4 py-3">
        <span className="material-icons-outlined text-[20px] text-brand-primary">business</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-theme-fg-muted uppercase tracking-wide">Business Profile</p>
          {effectiveCustomerId ? (
            <p className="text-sm font-semibold text-theme-fg truncate">
              {displayCustomerName ?? effectiveCustomerId}
            </p>
          ) : (
            <p className="text-sm text-amber-600 font-medium">
              No profile selected — use the sidebar to pick one
            </p>
          )}
        </div>
      </div>

      {/* Loading state */}
      {loadingAgentDetails && (
        <div className="mb-4 flex items-center gap-2 text-sm text-theme-fg-muted">
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Loading agent details...
        </div>
      )}

      {/* No agent ID warning */}
      {!selectedAgentId && (
        <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200">
          No agent selected. Please open this page from an agent's Edit button.
        </div>
      )}

      {/* Agent details error */}
      {agentDetailsError && (
        <div className="mb-4 px-4 py-2 rounded-lg text-sm bg-red-100 text-red-800 border border-red-300">
          {agentDetailsError}
        </div>
      )}

      {/* Form fields — shown once agent details are loaded */}
      {selectedAgentId && parsedDetails && (
        <>
          <AgentProfilePictureUploader
            currentPictureUrl={pictureUrl}
            newPictureFile={newPicture}
            onFileChange={setNewPicture}
          />

          {/* Agent Name */}
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">Agent Name</label>
            <input
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              disabled={isDisabled}
            />
          </div>

          {/* Model — same-provider switching only; see ModelSelectionSection.lockProvider */}
          <ModelSelectionSection
            selectedModel={model}
            agentType={parsedDetails?.agentType}
            onModelChange={(id) => setModel(id)}
            lockProvider={agentDetails?.provider || undefined}
          />

          {/* Instructions */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-gray-700 font-medium">Instructions</label>
              <button
                type="button"
                onClick={() => setShowTemplate(!showTemplate)}
                className="text-sm text-brand-primary hover:text-brand-primary-dark transition-colors"
              >
                {showTemplate ? "Hide Template" : "Show Template"}
              </button>
            </div>
            {showTemplate && (
              <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <TemplateReference onCopyToInstructions={(template) => setInstructions(template)} />
              </div>
            )}
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={10}
              className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              disabled={isDisabled}
            />
          </div>

          {/* Conversation Starters */}
          <ConversationStartersSection
            starters={conversationStarters}
            onChange={setConversationStarters}
            disabled={isDisabled}
          />

          {/* Tools Selection */}
          <ToolsSelectionSection
            selectedTools={selectedTools}
            disabled={isDisabled}
            onToolToggle={handleToolToggle}
            agentType={parsedDetails?.agentType}
          />

          {/* Knowledge Files */}
          <KnowledgeFilesSection
            knowledgeFiles={knowledgeFiles}
            newKnowledgeFiles={newKnowledgeFiles}
            savingKnowledgeFiles={savingKnowledgeFiles}
            disabled={isDisabled}
            onRemoveFile={removeKnowledgeFile}
            onRemoveNewFile={removeNewFile}
            onAddFiles={addKnowledgeFiles}
            onSave={handleSaveKnowledgeFiles}
            hasChanges={hasKnowledgeFileChanges}
          />

          {/* Memory (SGL-417 / SGL-115): saved entry by entry, so it is edited in place, not by the form's Update button */}
          {effectiveCustomerId && parsedDetails?.agentType !== "onboarding" && (
            <MemorySection customerId={effectiveCustomerId} agentId={selectedAgentId} disabled={isDisabled} />
          )}
        </>
      )}

      {/* Status message */}
      {message && (
        <div
          className={`mb-4 px-4 py-2 rounded-lg text-sm ${
            message.startsWith("✅")
              ? "sgl-banner-sage border"
              : message.includes("Loading") ||
                message.includes("Updating") ||
                message.includes("Uploading")
              ? "sgl-banner-sage border"
              : "bg-red-100 text-red-800 border border-red-300"
          }`}
        >
          {message}
        </div>
      )}

      {/* Submit button */}
      {selectedAgentId && parsedDetails && (
        <button
          onClick={handleSubmit}
          disabled={
            isLoading ||
            savingKnowledgeFiles ||
            !agentDetails ||
            agentName.trim().length === 0 ||
            instructions.trim().length === 0
          }
          className={`w-full py-2 px-4 rounded-lg text-white ${
            isLoading || !agentDetails || !instructions
              ? "bg-brand-primary opacity-50 cursor-not-allowed"
              : "bg-brand-primary hover:bg-brand-primary-hover"
          }`}
        >
          {isLoading ? "Updating..." : "Update Agent Instructions"}
        </button>
      )}
    </div>
  );
};

export default EditAgentForm;
